import React, { useState, useEffect, useRef } from 'react';
import { C, fmt } from '../tokens.js';
import { Card, Badge, BkHdr, ImgOrEmoji } from '../components/ui.jsx';
import { D } from '../mock-data.js';

const RECENT  = ['Full Cream Milk', 'Haircut', 'Multigrain Bread', 'Eggs'];
const POPULAR = [
  {q:'Milk delivery', em:'🥛'}, {q:'Salon near me', em:'✂️'},
  {q:'Organic vegetables', em:'🥬'}, {q:'Fresh bread', em:'🍞'},
  {q:'Beard trim', em:'💈'}, {q:'Free-range eggs', em:'🥚'},
];

export const SearchScreen = ({goBack, navigate, initialCategory}) => {
  const [query, setQuery] = useState(initialCategory || '');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState(RECENT);
  const inputRef = useRef();

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Search across all window.D data, optionally restricted to a category.
  const doSearch = (q) => {
    if (!q.trim()) { setResults(null); return; }
    setLoading(true);
    setTimeout(() => {
      const ql = q.toLowerCase();
      const stores = [], products = [], services = [];

      Object.values(D.branches).flat().forEach(branch => {
        if (branch.name.toLowerCase().includes(ql) || branch.type.toLowerCase().includes(ql))
          stores.push(branch);
        branch.products?.forEach(p => {
          if (initialCategory && p.cat !== initialCategory) return;
          if (p.name.toLowerCase().includes(ql) || p.cat?.toLowerCase().includes(ql) || p.desc?.toLowerCase().includes(ql))
            products.push({ ...p, branchName: branch.name, branchId: branch.id, branch });
        });
        branch.services?.forEach(s => {
          if (s.name.toLowerCase().includes(ql))
            services.push({ ...s, branchName: branch.name, branchId: branch.id, branch });
        });
      });
      setResults({ stores, products, services });
      setLoading(false);
      setRecent(r => [q, ...r.filter(x => x !== q)].slice(0, 6));
    }, 300);
  };

  useEffect(() => { doSearch(query); }, [query]);

  const total = results ? results.stores.length + results.products.length + results.services.length : 0;

  const SectionHead = ({title, count}) => (
    <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', margin:'16px 0 10px'}}>
      <span style={{fontSize:14, fontWeight:700, color:C.text}}>{title}</span>
      <Badge bg="#f5f5f4" color={C.textMed} sm>{count}</Badge>
    </div>
  );

  return (
    <div className="fade-in" style={{flex:1, display:'flex', flexDirection:'column'}}>
      {/* Search bar */}
      <div style={{padding:'12px 20px', background:C.bg, borderBottom:`1px solid ${C.border}`, display:'flex', gap:10, alignItems:'center', flexShrink:0}}>
        <button onClick={goBack} style={{width:38, height:38, borderRadius:12, background:C.white, border:`1px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0}}>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M11 13L7 9l4-4" stroke={C.text} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
        <div style={{flex:1, display:'flex', alignItems:'center', gap:8, padding:'10px 14px', background:C.white, borderRadius:14, border:`1.5px solid ${query?C.primary:C.border}`, transition:'border .2s'}}>
          <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><circle cx="7" cy="7" r="5" stroke={C.textMed} strokeWidth="1.5"/><path d="M11 11l3 3" stroke={C.textMed} strokeWidth="1.5" strokeLinecap="round"/></svg>
          <input ref={inputRef} value={query} onChange={e=>setQuery(e.target.value)}
            placeholder="Search stores, products, services…"
            style={{flex:1, border:'none', outline:'none', fontSize:14, color:C.text, background:'none'}}/>
          {query && <button onClick={()=>setQuery('')} style={{background:'none', border:'none', color:C.textMed, fontSize:18, cursor:'pointer', lineHeight:1}}>×</button>}
        </div>
      </div>

      <div style={{flex:1, overflowY:'auto', padding:'0 20px 24px'}}>

        {/* No query: show recent + popular */}
        {!query && (
          <>
            {recent.length > 0 && (
              <div style={{marginTop:20}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12}}>
                  <span style={{fontSize:14, fontWeight:700, color:C.text}}>Recent searches</span>
                  <button onClick={()=>setRecent([])} style={{background:'none', border:'none', color:C.textMed, fontSize:12, fontWeight:600, cursor:'pointer'}}>Clear</button>
                </div>
                <div style={{display:'flex', flexDirection:'column', gap:2}}>
                  {recent.map((r,i) => (
                    <button key={i} onClick={()=>setQuery(r)}
                      style={{display:'flex', alignItems:'center', gap:10, padding:'11px 0', border:'none', borderBottom:`1px solid ${C.border}`, background:'none', cursor:'pointer', textAlign:'left', width:'100%'}}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 3v4l2.5 2.5M7 1a6 6 0 110 12A6 6 0 017 1z" stroke={C.textLight} strokeWidth="1.4" strokeLinecap="round"/></svg>
                      <span style={{fontSize:14, color:C.text, flex:1}}>{r}</span>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 10L10 3M10 3H5M10 3v5" stroke={C.textMed} strokeWidth="1.4" strokeLinecap="round"/></svg>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div style={{marginTop:24}}>
              <p style={{fontSize:14, fontWeight:700, color:C.text, marginBottom:12}}>Popular searches</p>
              <div style={{display:'flex', flexWrap:'wrap', gap:8}}>
                {POPULAR.map((p,i) => (
                  <button key={i} onClick={()=>setQuery(p.q)}
                    style={{display:'flex', alignItems:'center', gap:6, padding:'8px 14px', borderRadius:100, border:`1px solid ${C.border}`, background:C.white, cursor:'pointer', fontSize:13, color:C.text, fontWeight:500}}>
                    <span>{p.em}</span>{p.q}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Loading */}
        {loading && (
          <div style={{textAlign:'center', padding:'40px 20px'}}>
            <div className="pulse" style={{fontSize:32, marginBottom:8}}>🔍</div>
            <p style={{fontSize:13, color:C.textMed}}>Searching…</p>
          </div>
        )}

        {/* Results */}
        {results && !loading && total === 0 && (
          <div style={{textAlign:'center', padding:'48px 20px', marginTop:20}}>
            <div style={{fontSize:48, marginBottom:12}}>🔍</div>
            <p style={{fontSize:15, fontWeight:700, color:C.text}}>No results for "{query}"</p>
            <p style={{fontSize:13, color:C.textMed, marginTop:4}}>Try a different keyword or browse categories</p>
          </div>
        )}

        {results && !loading && total > 0 && (
          <>
            <p style={{fontSize:13, color:C.textMed, margin:'16px 0 4px'}}>{total} result{total!==1?'s':''} for <b style={{color:C.text}}>"{query}"</b></p>

            {/* Stores */}
            {results.stores.length > 0 && (
              <>
                <SectionHead title="Stores" count={results.stores.length}/>
                {results.stores.map(b => (
                  <div key={b.id} onClick={()=>navigate('branch',{branch:b,btype:b.services?'salons':'grocery'})}
                    style={{display:'flex', gap:12, alignItems:'center', padding:'12px 14px', borderRadius:16, background:C.white, border:`1px solid ${C.border}`, marginBottom:8, cursor:'pointer'}}>
                    <ImgOrEmoji src={D.storeImages[b.id]} fallback={b.em} size={44} radius={12} bg={b.bg} fontSize={22}/>
                    <div style={{flex:1}}>
                      <p style={{fontSize:14, fontWeight:700, color:C.text}}>{b.name}</p>
                      <p style={{fontSize:12, color:C.textMed}}>{b.type} · 📍 {b.dist} km</p>
                    </div>
                    <div style={{display:'flex', alignItems:'center', gap:3, flexShrink:0}}>
                      <svg width="11" height="11" viewBox="0 0 11 11" fill="#d97706"><polygon points="5.5,0.5 7,3.8 10.8,4.2 8.1,6.8 8.9,10.5 5.5,8.6 2.1,10.5 2.9,6.8 0.2,4.2 4,3.8"/></svg>
                      <span style={{fontSize:12, fontWeight:700, color:C.text}}>{b.rating}</span>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Products */}
            {results.products.length > 0 && (
              <>
                <SectionHead title="Products" count={results.products.length}/>
                {results.products.map((p,i) => (
                  <div key={i} onClick={()=>navigate('product',{product:p,branch:p.branch})}
                    style={{display:'flex', gap:12, alignItems:'center', padding:'12px 14px', borderRadius:16, background:C.white, border:`1px solid ${C.border}`, marginBottom:8, cursor:'pointer'}}>
                    <ImgOrEmoji src={D.productImages[p.id]} fallback={p.em} size={44} radius={12} bg="#e6f7fb" fontSize={22}/>
                    <div style={{flex:1, minWidth:0}}>
                      <div style={{display:'flex', alignItems:'center', gap:6, flexWrap:'wrap'}}>
                        <p style={{fontSize:14, fontWeight:700, color:C.primary}}>{p.name}</p>
                        {p.subscribable !== false && <Badge bg="#e6f7fb" color={C.primary} sm>📅 Subscribable</Badge>}
                      </div>
                      <p style={{fontSize:12, color:C.textMed}}>{p.branchName} · {p.cat}</p>
                    </div>
                    <div style={{textAlign:'right', flexShrink:0}}>
                      <p style={{fontSize:13, fontWeight:800, color:C.primary}}>from {fmt(p.variants[0].p)}</p>
                      <p style={{fontSize:10, color:C.textMed}}>{p.variants[0].l}</p>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Services */}
            {results.services.length > 0 && (
              <>
                <SectionHead title="Salon services" count={results.services.length}/>
                {results.services.map((s,i) => (
                  <div key={i} onClick={()=>navigate('booking-service',{service:s,branch:s.branch})}
                    style={{display:'flex', gap:12, alignItems:'center', padding:'12px 14px', borderRadius:16, background:C.white, border:`1px solid ${C.border}`, marginBottom:8, cursor:'pointer'}}>
                    <div style={{width:44, height:44, borderRadius:12, background:'#fce7f3', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0}}>✂️</div>
                    <div style={{flex:1}}>
                      <p style={{fontSize:14, fontWeight:700, color:C.text}}>{s.name}</p>
                      <p style={{fontSize:12, color:C.textMed}}>{s.branchName} · ⏱ {s.duration} min</p>
                    </div>
                    <p style={{fontSize:13, fontWeight:800, color:C.primary, flexShrink:0}}>{fmt(s.priceP)}</p>
                  </div>
                ))}
              </>
            )}
          </>
        )}
        <div style={{height:8}}/>
      </div>
    </div>
  );
};
